import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, keyframes } from '@mui/material';

const blinkAnimation = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const BlinkingCursor = () => (
  <Box
    component="span"
    sx={{
      width: '2px',
      height: '1.2em',
      backgroundColor: 'primary.main',
      display: 'inline-block',
      animation: `${blinkAnimation} 1s infinite`,
      marginLeft: '2px',
      verticalAlign: 'text-bottom'
    }}
  />
);

interface StreamingTextProps {
  content: string;
  isStreaming: boolean;
  showCursor?: boolean;
  animationSpeed?: number;
  onAnimationComplete?: () => void;
}

/**
 * StreamingText Component
 * 
 * Displays text with a typewriter effect during streaming.
 * Shows content progressively as it's being generated.
 */
const StreamingText: React.FC<StreamingTextProps> = ({
  content,
  isStreaming,
  showCursor = true,
  animationSpeed = 20,
  onAnimationComplete
}) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<NodeJS.Timeout | null>(null);
  const lastContentLength = useRef(0);

  useEffect(() => {
    if (animationRef.current) {
      clearTimeout(animationRef.current);
    }

    if (isStreaming) {
      setDisplayedContent(content);
      setIsAnimating(false);
      lastContentLength.current = content.length;
    } else {
      if (displayedContent !== content) {
        setIsAnimating(true);
        setDisplayedContent('');
        let charIndex = 0;

        const typeCharacter = () => {
          if (charIndex < content.length) {
            setDisplayedContent(prev => prev + content[charIndex]);
            charIndex++;
            animationRef.current = setTimeout(typeCharacter, animationSpeed);
          } else {
            setIsAnimating(false);
            lastContentLength.current = content.length;
            if (onAnimationComplete) {
              onAnimationComplete();
            }
          }
        };

        if (content.length > 0) {
          typeCharacter();
        } else {
          setDisplayedContent('');
          setIsAnimating(false);
          lastContentLength.current = 0;
          if (onAnimationComplete) {
            onAnimationComplete();
          }
        }
      } else {
        setIsAnimating(false);
        lastContentLength.current = content.length;
      }
    }

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [content, isStreaming, animationSpeed, onAnimationComplete, displayedContent]);

  useEffect(() => {
    if (isStreaming && content === '') {
      if (displayedContent !== '') {
        setDisplayedContent('');
      }
      lastContentLength.current = 0;
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
      setIsAnimating(false);
    }
  }, [isStreaming, content, displayedContent]);

  const formatContent = (text: string) => {
    // Enhanced text processing for better formatting during streaming
    const processedText = text
      // Fix encoding issues first
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      // Fix UTF-8 smart quotes and special characters
      .replace(/â€™/g, "'")
      .replace(/â€œ/g, '"')
      .replace(/â€/g, '"')
      .replace(/â€"/g, '-')
      .replace(/â€¦/g, '...')
      .replace(/Â/g, '')
      .replace(/'/g, "'")
      .replace(/'/g, "'")
      .replace(/"/g, '"')
      .replace(/"/g, '"')
      .replace(/–/g, '-')
      .replace(/—/g, '-')
      .trim();

    // Split content into lines and format
    return processedText.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return null;

      // Handle markdown headers (### Header, ## Header, # Header)
      if (trimmedLine.match(/^#{1,6}\s+/)) {
        const headerLevel = (trimmedLine.match(/^#+/) || [''])[0].length;
        const headerText = trimmedLine.replace(/^#{1,6}\s+/, '').trim();
        
        const variant = headerLevel === 1 ? 'h4' : 
                      headerLevel === 2 ? 'h5' : 
                      headerLevel === 3 ? 'h6' : 'subtitle1';
        
        return (
          <Typography 
            key={index} 
            variant={variant}
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mt: index > 0 ? 3 : 0,
              mb: 2,
              borderBottom: headerLevel <= 2 ? '2px solid' : '1px solid',
              borderColor: 'primary.light',
              pb: 1
            }}
          >
            {headerText}
          </Typography>
        );
      }

      // Check if it's a section header with time (e.g., "Pre-Reading Activities (10-15 minutes)")
      if (trimmedLine.match(/.*\(\d+-\d+\s*minutes?\)/) && !trimmedLine.startsWith('-') && !trimmedLine.match(/^\d+\./) && !trimmedLine.match(/^#{1,6}\s+/)) {
        const timeMatch = trimmedLine.match(/\((\d+-\d+\s*minutes?)\)/);
        const sectionTitle = trimmedLine.replace(/\s*\(\d+-\d+\s*minutes?\)/, '').trim();
        
        return (
          <Box key={index} sx={{ mt: index > 0 ? 3 : 0, mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.light',
                pb: 1
              }}
            >
              {sectionTitle}
              {timeMatch && (
                <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                  ({timeMatch[1]})
                </Typography>
              )}
            </Typography>
          </Box>
        );
      }

      // Check if it's an activity header (numbered item like "1. Pre-Reading Discussion (2-3 minutes)" or "3. Text Analysis Practice")
      if (trimmedLine.match(/^\d+\.\s+/)) {
        const activityTitle = trimmedLine.replace(/^\d+\.\s+/, '');
        const timeMatch = activityTitle.match(/\((\d+-\d+\s*minutes?)\)/);
        const title = activityTitle.replace(/\s*\(\d+-\d+\s*minutes?\)/, '').trim();
        const numberMatch = trimmedLine.match(/^\d+\./);
        
        // Check if this is a major section (like "3. Text Analysis Practice")
        const isMajorSection = title.length > 20 || timeMatch;
        
        return (
          <Box key={index} sx={{ mt: isMajorSection ? 3 : 2, mb: 1 }}>
            <Typography 
              variant={isMajorSection ? "h6" : "subtitle1"} 
              sx={{ 
                fontWeight: 600,
                color: isMajorSection ? 'primary.main' : 'secondary.main',
                borderBottom: isMajorSection ? '1px solid' : 'none',
                borderColor: 'primary.light',
                pb: isMajorSection ? 0.5 : 0
              }}
            >
              {numberMatch ? numberMatch[0] : ''} {title}
              {timeMatch && (
                <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                  ({timeMatch[1]})
                </Typography>
              )}
            </Typography>
          </Box>
        );
      }

      // Check for special instruction types (like "Instructions:", "Activity A:", etc.)
      if (trimmedLine.match(/^(Instructions|Activity [A-Z]|Assessment Criteria|Rubric Suggestions):/)) {
        const match = trimmedLine.match(/^([^:]+):\s*(.*)/);
        if (match) {
          const label = match[1];
          const content = match[2];
          
          return (
            <Box key={index} sx={{ mt: 2, mb: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  color: 'secondary.main',
                  display: 'block'
                }}
              >
                {label}:
              </Typography>
              {content && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5,
                    fontStyle: label === 'Instructions' ? 'italic' : 'normal'
                  }}
                >
                  {content}
                </Typography>
              )}
            </Box>
          );
        }
      }

      // Check if it's a bullet point (- item or * item)
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        const bulletText = trimmedLine.substring(2).trim();
        
        // Check if it has a label (like "Words to explore:")
        const colonIndex = bulletText.indexOf(':');
        let label = '';
        let text = bulletText;
        
        if (colonIndex > 0 && colonIndex < 50) {
          label = bulletText.substring(0, colonIndex).trim();
          text = bulletText.substring(colonIndex + 1).trim();
        }
        
        return (
          <Box key={index} sx={{ ml: 2, mb: 0.3 }}>
            <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
              <Box component="span" sx={{ display: 'inline-block', width: '8px', mr: 1 }}>•</Box>
              {label && (
                <Typography component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                  {label}:
                </Typography>
              )}
              <Typography component="span">
                {text}
              </Typography>
            </Typography>
          </Box>
        );
      }

      // Check if it's a standalone label (ends with colon)
      if (trimmedLine.endsWith(':') && trimmedLine.length < 100) {
        return (
          <Typography 
            key={index} 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              mt: 1,
              mb: 0.5,
              color: 'text.primary'
            }}
          >
            {trimmedLine}
          </Typography>
        );
      }

      // Check if it's a title (starts with "Title:" or is first line and short)
      const isTitle = trimmedLine.startsWith('Title:') || 
                    (index === 0 && trimmedLine.length < 100 && !trimmedLine.includes('.'));
      
      if (isTitle) {
        const titleText = trimmedLine.replace(/^Title:\s*/, '');
        return (
          <Typography 
            key={index} 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              mb: 2,
              color: 'primary.main'
            }}
          >
            {titleText}
          </Typography>
        );
      }

      // Handle inline formatting for regular text
      const formatInlineText = (text: string) => {
        // Handle character names and dialogue - handle both **NAME**: and NAME**: patterns
        let formattedText = text
          .replace(/\*\*([^*]+)\*\*:/g, '<strong>$1</strong>:')  // **CHARACTER**:
          .replace(/([A-Z][A-Z\s.]+)\*\*:/g, '<strong>$1</strong>:')  // CHARACTER**:
          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')   // **text**
          .replace(/__(.*?)__/g, '<strong>$1</strong>');        // __text__
        
        // Handle italic text (*text* or _text_)
        formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
        formattedText = formattedText.replace(/_(.*?)_/g, '<em>$1</em>');
        
        return formattedText;
      };

      // Check if this is drama content by looking for bold character names pattern (improved for multi-word names)
      const isDramaLine = /\*\*[A-Z][^*]*\*\*:/.test(trimmedLine) || 
                          /[A-Z][^:]*\*\*:/.test(trimmedLine) || 
                          trimmedLine.includes('**');
      
      // Regular paragraph text
      const formattedLine = formatInlineText(trimmedLine);
      
      return (
        <Typography 
          key={index} 
          variant="body1" 
          component={isDramaLine ? "div" : "p"}
          paragraph={!isDramaLine}
          sx={{ 
            lineHeight: isDramaLine ? 1.4 : 1.6,
            mb: isDramaLine ? 0.5 : 1,
            textAlign: 'left',
            whiteSpace: isDramaLine ? 'pre-wrap' : 'normal'
          }}
          dangerouslySetInnerHTML={{ __html: formattedLine }}
        />
      );
    }).filter(Boolean);
  };

  return (
    <Box sx={{ minHeight: '2em', position: 'relative' }}>
      {displayedContent ? (
        <Box>
          {formatContent(displayedContent)}
          {(isStreaming || isAnimating) && showCursor && <BlinkingCursor />}
        </Box>
      ) : isStreaming ? (
        <Box display="flex" alignItems="center">
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Starting generation...
          </Typography>
          {showCursor && <BlinkingCursor />}
        </Box>
      ) : null}
    </Box>
  );
};

export default StreamingText; 